import { Directive, Input, ViewContainerRef, HostListener, Renderer2, OnInit, OnDestroy, EmbeddedViewRef, Optional, Inject, forwardRef, ChangeDetectorRef } from '@angular/core';
import { MenuComponent } from './menu.component';
import { MenuItemComponent } from './menu-item/menu-item.component';
import { MenuService } from './menu.service';
import { Subscription } from 'rxjs';
import { PositioningService } from './positioning.service';
import { MenuPosition } from './menu-position';

@Directive({
    selector: '[menuFor]',
    providers: [
        PositioningService,
    ],
    standalone: false
})
export class MenuForDirective implements OnInit, OnDestroy {
    @Input('menuFor')
    menu: MenuComponent;

    @Input()
    public menuPosition: MenuPosition = { position: 'after', verticalPosition: 'center' };

    @Input()
    public maxHeight: string = null;

    public _subscription: () => void;
    public _mouseHoveredSubscriptions: Subscription = new Subscription();
    public _activeView: EmbeddedViewRef<any>;
    public _position: any;

    @HostListener('click', ['$event'])
    public onClick(e: MouseEvent) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        this.open();
    }

    ngOnInit() {
        this.menu.onClosing.subscribe(() => {
            if (this._subscription)
                this._subscription();

            this.menu.closed = true;
            this._viewContainerRef.clear();
            this._activeView = null;
        });

        this.menu.onOpening.subscribe(() => {
            if (this._subscription)
                this._subscription;

            this._viewContainerRef.clear();
            this.menu.closed = false;
            this._activeView = this._viewContainerRef.createEmbeddedView(this.menu.template);
            let parentElement = this._viewContainerRef.element.nativeElement;
            this._viewContainerRef;
            let node = this.getMenuContainer();
            if (this.maxHeight)
                this._renderer.setStyle(node.firstElementChild, 'max-height', this.maxHeight);

            let initialPosition = { x: 0, y: 0 };
            let parentMenuWrapper;
            if (node) {
                this._renderer.setStyle(node, 'position', 'absolute');

                if (!this._menuItem)
                    initialPosition = this._positioningService.getInitialPosition(parentElement, node, this.menuPosition);
                else {
                    parentMenuWrapper = parentElement.offsetParent.firstElementChild;
                    initialPosition.y = parentElement.offsetTop - parentMenuWrapper.scrollTop;
                    initialPosition.x = this._menuItem.elementRef.nativeElement.offsetWidth;

                    //if the menu is forced to reflow then we must set the y position to the bottom of the parent element.
                    if (this._positioningService.getPosition(initialPosition, node).y !== initialPosition.y)
                        initialPosition.y += parentElement.offsetHeight + 1;
                }
                this._position = this._positioningService.getPosition(initialPosition, node);

                if (parentMenuWrapper)
                    this._positioningService.setPosition(node, this._position, parentMenuWrapper.offsetWidth - parentMenuWrapper.clientWidth);
                else
                    this._positioningService.setPosition(node, this._position);

                this._subscription = this._renderer.listen('document', 'click', () => {
                    this.close();
                });
            }
        });
    }

    getMenuContainer(): HTMLElement {
        return this._activeView.rootNodes.find(x => x.id && x.id === 'menu');
    }

    ngOnDestroy() {
        if (this._subscription)
            this._subscription();

        this._mouseHoveredSubscriptions.unsubscribe();
    }

    constructor(
        public _viewContainerRef: ViewContainerRef,
        public _renderer: Renderer2,
        @Optional() @Inject(forwardRef(() => MenuService)) public _menuService: MenuService,
        @Optional() @Inject(forwardRef(() => MenuItemComponent)) public _menuItem: MenuItemComponent,
        public _positioningService: PositioningService,
    ) {
        if (this._menuItem) { 
            this._menuItem.opensChildMenu = true;

            let el = this._menuItem.elementRef.nativeElement;
            this._mouseHoveredSubscriptions.add(this._renderer.listen(el, 'mouseover', () => {
                this.open();
            }));

            this._menuService.onItemEntered.subscribe((item) => {
                if (this._menuItem !== item)
                    this.close();
            });
            this._menuService.onMenuOpened.subscribe((menu) => {
                if (this.menu !== menu)
                    this.close();
            });
        }
    }

    public close(): void {
        this.menu.onClosing.emit();
    }

    public open(): void {
        this.menu.onOpening.emit();

        if (this._menuService)
            this._menuService.onMenuOpened.emit(this.menu);
    }
}
